import {useEffect, useState} from "react";
import {MixpanelCurrentGroupDetails, ViaType} from "@/api/dto/mixpanel";
import {Roster} from "@/api/dto/dto";
import FileHelpers from "@/helpers/file_helpers";

export type MixpanelGenericProperties = {
  viewPortWidth: number;
  viewPortHeight: number;
  $os: string;
  device: string;
  currentApplicationLocation: string;
  pageUrl: string;
  via: Nullable<ViaType>
}

export function useMixpanel(
  returnToRoute: Nullable<string> = null
) {
  const [mixpanelEventProperties, setMixpanelEventProperties] = useState<MixpanelGenericProperties | null>(null);

  useEffect(() => {
    setTimeout(() => {
      const currentApplicationLocationAndVia = getCurrentApplicationLocationAndVia(
        returnToRoute
      );

      setMixpanelEventProperties({
        viewPortWidth: window.innerWidth,
        viewPortHeight: window.innerHeight,
        $os: getOperatingSystem(),
        device: getDeviceType(),
        currentApplicationLocation: currentApplicationLocationAndVia.currentLocation,
        pageUrl: window.location.href,
        via: currentApplicationLocationAndVia.via
      });
    });
  }, [returnToRoute]);

  return {
    mixpanelEventProperties,
    getCurrentGroupDetails, 
    calculateFileSize, 
    getOperatingSystem, 
    getDeviceType, 
    getCurrentApplicationLocationAndVia
  };
}

const getCurrentApplicationLocationAndVia = (returnToRoute: Nullable<string>) => {
  const pathName = window.location.pathname;
  const href = window.location.href;
  if (pathName === "/login") {
    return {
      currentLocation: "Login",
      via: returnToRoute?.includes("via=email") ? ViaType.EMAIL : null
    };
  } else if (pathName === "/clients") {
    return {
      currentLocation: "Clients List",
      via: href.includes("via=email") ? ViaType.EMAIL : ViaType.CLIENT_LIST
    };
  } else if (/^\/clients\/profile(\/.*)?$/.test(pathName)) {
    return {
      currentLocation: "Client Profile",
      via: href.includes("via=email") ? ViaType.EMAIL : ViaType.CLIENT_PROFILE
    };
  } else if (/^\/admin(\/.*)?$/.test(pathName)) {
    return {
      currentLocation: "Admin",
      via: href.includes("via=email") ? ViaType.EMAIL : ViaType.ADMIN
    };
  } else if (pathName === "/notifications") {
    return {
      currentLocation: "Notifications",
      via: href.includes("via=email") ? ViaType.EMAIL : ViaType.NOTIFICATIONS
    };
  } else if (pathName === "/profile") {
    return {
      currentLocation: "User Profile",
      via: href.includes("via=email") ? ViaType.EMAIL : ViaType.USER_PROFILE
    };
  } else if(pathName === "/tasks"){
    return {
      currentLocation: "Tasks",
      via: href.includes("via=email") ? ViaType.EMAIL : ViaType.TASKS
    };
  } else if(pathName === "/settings/form_templates"){
    return {
      currentLocation: "Form Templates",
      via: href.includes("via=email") ? ViaType.EMAIL : ViaType.FORM_TEMPLATES
    };  
  }else {
    return {
      currentLocation: "Clients List",
      via: ViaType.CLIENT_LIST
    };
  }
};

const getDeviceType = () => {
  const userAgent = navigator.userAgent;

  if (/iPhone/i.test(userAgent)) return "iPhone";
  if(/iPad/.test(userAgent)) return "iPad";
  if (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) return "Tablet";
  if (/Android/i.test(userAgent)) return "Android";
  if (/Windows/i.test(userAgent) ||
      /Linux/i.test(userAgent) ||
      /Mac/i.test(userAgent)) return "Desktop";

  return "Desktop";
};

const getOperatingSystem = () => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes("Android")) return "Android";
  if (userAgent.includes("like Mac")) return "iOS";
  if (userAgent.includes("Win")) return "Windows";
  if (userAgent.includes("Mac")) return "macOS";
  if (userAgent.includes("X11") || userAgent.includes("Linux")) return "Linux";

  return "Unknown OS";
};

const getCurrentGroupDetails = (currentRoster : Roster): MixpanelCurrentGroupDetails => {
  return {
    currentGroupId: currentRoster.id,
    currentGroupName: currentRoster.name,
    currentGroupType: currentRoster.type,
    currentStateName: currentRoster.address.state,
    currentOrganizationId: currentRoster.organization.id,
    currentOrganizationName: currentRoster.organization.name
  };
};

const calculateFileSize = (files: File[]) => {
  if (files && files.length > 0) {
    const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
    return FileHelpers.formatBits(totalSize);
  }
};
